import {
  FLEET_LICENSE_CLASSES,
  FleetAreaCode,
  FleetLicenseCheckResult,
  PermissionCode,
  type FleetLicenseClass,
  type FleetLicenseCheckResult as CheckResult,
} from '@gb-ticket/shared-types';
import { type FormEvent, useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { PersonChip } from '@/platform/components/person-chip';
import {
  FLEET_DOCUMENT_KIND_LABELS,
  formatDeDate,
  formatDeDateTime,
} from '@/products/fleet/features/fleet-labels';
import {
  fleetPanelClassName,
  fleetTableFrameClassName,
} from '@/products/fleet/features/fleet-panel';
import { fleetAreaOn, useFleetAreas } from '@/products/fleet/features/use-fleet-areas';
import { FleetDocumentDeleteButton } from '@/products/fleet/features/fleet-document-delete-button';
import {
  fleetDocumentDownloadHref,
  useFleetDriver,
  useFleetDriverDocuments,
  useFleetPolicy,
  useRecordFleetLicenseCheck,
  useUpsertFleetDriverProfile,
} from '@/products/fleet/features/use-fleet';
import { FLEET_DRIVERS_PATH } from '@/products/fleet/fleet-paths';

const RESULT_LABELS: Record<CheckResult, string> = {
  valid: 'gültig',
  invalid: 'ungültig',
  restricted: 'eingeschränkt',
};

type ProfileDraft = {
  classes: FleetLicenseClass[];
  cardValidUntil: string;
  entitlementValidUntil: string;
};

type CheckDraft = {
  checkedAt: string;
  result: CheckResult;
  classes: FleetLicenseClass[];
  cardValidUntil: string;
  note: string;
};

export function FleetDriverDetailPage() {
  const { userId = '' } = useParams();
  const me = useMe();
  const areas = useFleetAreas();
  const catalog = areas.isError ? undefined : areas.data;
  const complianceOn = fleetAreaOn(catalog, FleetAreaCode.COMPLIANCE);
  const instructionOn = fleetAreaOn(catalog, FleetAreaCode.INSTRUCTION);
  const complianceManage = hasPermission(me.data, PermissionCode.FLEET_COMPLIANCE_MANAGE);
  const driver = useFleetDriver(userId);
  const fleetPolicy = useFleetPolicy();
  usePageTitle(driver.data?.displayName ?? 'Fahrerakte');
  const saveProfile = useUpsertFleetDriverProfile(userId);
  const recordCheck = useRecordFleetLicenseCheck(userId);
  const driverDocuments = useFleetDriverDocuments(userId, complianceOn && complianceManage);
  const photoAllowed = fleetPolicy.data?.allowLicensePhoto === true;
  const photoRequired = photoAllowed && (fleetPolicy.data?.licensePhotoRequired ?? true);
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);

  const [profileDraft, setProfileDraft] = useState<ProfileDraft | null>(null);
  const [checkDraft, setCheckDraft] = useState<CheckDraft | null>(null);

  const savedProfile = driver.data?.profile;
  const savedCheck = driver.data?.latestCheck;
  const profileValues: ProfileDraft = profileDraft ?? {
    classes: savedProfile?.licenseClasses?.length
      ? savedProfile.licenseClasses
      : savedCheck?.classesConfirmed?.length
        ? savedCheck.classesConfirmed
        : ['B'],
    cardValidUntil: savedProfile?.cardValidUntil ?? savedCheck?.cardValidUntil ?? '',
    entitlementValidUntil: savedProfile?.entitlementValidUntil ?? '',
  };

  const checkValues: CheckDraft = checkDraft ?? {
    checkedAt: savedCheck?.checkedAt ?? new Date().toISOString().slice(0, 10),
    result: savedCheck?.result ?? FleetLicenseCheckResult.VALID,
    classes: savedCheck?.classesConfirmed?.length
      ? savedCheck.classesConfirmed
      : savedProfile?.licenseClasses?.length
        ? savedProfile.licenseClasses
        : ['B'],
    cardValidUntil: savedCheck?.cardValidUntil ?? savedProfile?.cardValidUntil ?? '',
    note: savedCheck?.note ?? '',
  };

  function toggleClass(
    current: FleetLicenseClass[],
    value: FleetLicenseClass,
  ): FleetLicenseClass[] {
    return current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
  }

  async function onProfileSubmit(event: FormEvent) {
    event.preventDefault();
    if (profileValues.classes.length === 0) {
      toast.error('Bitte mindestens eine Führerscheinklasse wählen.');
      return;
    }
    try {
      await saveProfile.mutateAsync({
        licenseClasses: profileValues.classes,
        cardValidUntil: profileValues.cardValidUntil || null,
        entitlementValidUntil: profileValues.entitlementValidUntil || null,
      });
      setProfileDraft(null);
      toast.success('Profil gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Das Profil konnte nicht gespeichert werden.',
      );
    }
  }

  async function onCheckSubmit(event: FormEvent) {
    event.preventDefault();
    if (checkValues.classes.length === 0) {
      toast.error('Bitte die bestätigten Klassen angeben.');
      return;
    }
    if (photoRequired && !evidenceFile) {
      toast.error('Bitte ein Führerschein-Foto zur Kontrolle hochladen.');
      return;
    }
    try {
      await recordCheck.mutateAsync({
        checkedAt: checkValues.checkedAt,
        result: checkValues.result,
        classesConfirmed: checkValues.classes,
        cardValidUntil: checkValues.cardValidUntil || null,
        note: checkValues.note.trim() || undefined,
        ...(evidenceFile ? { file: evidenceFile } : {}),
      });
      setEvidenceFile(null);
      setCheckDraft(null);
      setProfileDraft(null);
      toast.success('Führerscheinkontrolle gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Führerscheinkontrolle konnte nicht gespeichert werden.',
      );
    }
  }

  if (driver.isPending) {
    return <p className="text-sm text-muted-foreground">Fahrerakte wird geladen …</p>;
  }

  if (driver.isError || !driver.data) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-destructive">
          {driver.error instanceof ApiRequestError
            ? driver.error.message
            : 'Fahrerakte nicht gefunden.'}
        </p>
        <Button asChild variant="outline">
          <Link to={FLEET_DRIVERS_PATH}>Zurück</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PlatformPageHeader
        title={
          <PersonChip
            displayName={driver.data.displayName}
            profilePhotoUrl={driver.data.profilePhotoUrl}
            avatarClassName="size-10"
          />
        }
        description={`${driver.data.email}${
          complianceOn && instructionOn
            ? ' · Führerscheinkontrolle und UVV-Unterweisung'
            : complianceOn
              ? ' · Führerscheinkontrolle'
              : instructionOn
                ? ' · UVV-Unterweisung'
                : ''
        }${driver.data.technician ? ' · Monteur' : ''}`}
      />
      <Button asChild variant="outline" size="sm">
        <Link to={FLEET_DRIVERS_PATH}>Alle Fahrer</Link>
      </Button>

      <section className={`${fleetPanelClassName} space-y-2`}>
        <h2 className="text-base font-semibold">Aktueller Stand</h2>
        {complianceOn ? (
          <p className="text-sm">
            Letzte Führerscheinkontrolle:{' '}
            {driver.data.latestCheck
              ? `${formatDeDate(driver.data.latestCheck.checkedAt)} (${RESULT_LABELS[driver.data.latestCheck.result]}), fällig ${formatDeDate(driver.data.latestCheck.nextCheckDue)}`
              : 'noch keine'}
          </p>
        ) : null}
        {instructionOn ? (
          <p className="text-sm">
            Letzte UVV-Unterweisung:{' '}
            {driver.data.latestInstruction
              ? `${formatDeDate(driver.data.latestInstruction.instructedOn)} · ${driver.data.latestInstruction.title}`
              : 'noch keine'}
          </p>
        ) : null}
      </section>

      {complianceOn ? (
        <section className="space-y-3">
          <h2 className="text-base font-semibold">Profil</h2>
          <form className={`${fleetPanelClassName} space-y-3`} onSubmit={onProfileSubmit}>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Führerscheinklassen</legend>
              <div className="flex flex-wrap gap-2">
                {FLEET_LICENSE_CLASSES.map((value) => (
                  <label key={value} className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={profileValues.classes.includes(value)}
                      onChange={() =>
                        setProfileDraft({
                          ...profileValues,
                          classes: toggleClass(profileValues.classes, value),
                        })
                      }
                    />
                    {value}
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="card-valid">Kartenablauf</Label>
                <Input
                  id="card-valid"
                  type="date"
                  value={profileValues.cardValidUntil}
                  onChange={(event) =>
                    setProfileDraft({ ...profileValues, cardValidUntil: event.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="entitlement-valid">Berechtigung bis (C/D)</Label>
                <Input
                  id="entitlement-valid"
                  type="date"
                  value={profileValues.entitlementValidUntil}
                  onChange={(event) =>
                    setProfileDraft({
                      ...profileValues,
                      entitlementValidUntil: event.target.value,
                    })
                  }
                />
              </div>
            </div>
            <Button type="submit" disabled={saveProfile.isPending}>
              Profil speichern
            </Button>
          </form>
        </section>
      ) : null}

      {complianceOn ? (
        <section className="space-y-3">
          <h2 className="text-base font-semibold">Führerscheinkontrolle</h2>
          <form className={`${fleetPanelClassName} space-y-3`} onSubmit={onCheckSubmit}>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="check-at">Kontrolldatum</Label>
                <Input
                  id="check-at"
                  type="date"
                  value={checkValues.checkedAt}
                  onChange={(event) =>
                    setCheckDraft({ ...checkValues, checkedAt: event.target.value })
                  }
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="check-result">Ergebnis</Label>
                <Select
                  value={checkValues.result}
                  onValueChange={(value) =>
                    setCheckDraft({
                      ...checkValues,
                      result: value as CheckResult,
                    })
                  }
                >
                  <SelectTrigger id="check-result" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={FleetLicenseCheckResult.VALID}>gültig</SelectItem>
                    <SelectItem value={FleetLicenseCheckResult.RESTRICTED}>
                      eingeschränkt
                    </SelectItem>
                    <SelectItem value={FleetLicenseCheckResult.INVALID}>ungültig</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="check-card">Kartenablauf</Label>
                <Input
                  id="check-card"
                  type="date"
                  value={checkValues.cardValidUntil}
                  onChange={(event) =>
                    setCheckDraft({ ...checkValues, cardValidUntil: event.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="check-note">Notiz</Label>
                <Input
                  id="check-note"
                  value={checkValues.note}
                  onChange={(event) => setCheckDraft({ ...checkValues, note: event.target.value })}
                />
              </div>
            </div>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Bestätigte Klassen</legend>
              <div className="flex flex-wrap gap-2">
                {FLEET_LICENSE_CLASSES.map((value) => (
                  <label key={value} className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={checkValues.classes.includes(value)}
                      onChange={() =>
                        setCheckDraft({
                          ...checkValues,
                          classes: toggleClass(checkValues.classes, value),
                        })
                      }
                    />
                    {value}
                  </label>
                ))}
              </div>
            </fieldset>
            {photoAllowed ? (
              <div className="space-y-1">
                <Label htmlFor="license-photo">Führerschein-Foto</Label>
                <Input
                  id="license-photo"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
                  required={photoRequired}
                  onChange={(event) => setEvidenceFile(event.target.files?.[0] ?? null)}
                />
                <p className="text-sm text-muted-foreground">
                  {photoRequired
                    ? 'Pflicht. Es gilt das Kontrolldatum.'
                    : 'Optional. Es gilt das Kontrolldatum.'}
                </p>
              </div>
            ) : null}
            <Button type="submit" disabled={recordCheck.isPending}>
              Führerscheinkontrolle speichern
            </Button>
          </form>
        </section>
      ) : null}

      {complianceOn &&
      complianceManage &&
      driverDocuments.data &&
      driverDocuments.data.items.length > 0 ? (
        <section className="space-y-3">
          <h2 className="text-base font-semibold">Führerschein-Foto</h2>
          <ul className={`${fleetTableFrameClassName} divide-y text-sm`}>
            {driverDocuments.data.items.map((doc) => {
              const linked = driver.data.checks.find((check) => check.id === doc.licenseCheckId);
              return (
                <li key={doc.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
                  <div>
                    <p className="font-medium">{doc.fileName}</p>
                    <p className="text-muted-foreground">
                      {linked
                        ? `Kontrolldatum ${formatDeDate(linked.checkedAt)}`
                        : `${FLEET_DOCUMENT_KIND_LABELS[doc.kind]} · ${formatDeDateTime(doc.createdAt)}`}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <a
                      href={fleetDocumentDownloadHref(doc.id)}
                      className="text-sm font-medium text-primary underline-offset-4 hover:underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Herunterladen
                    </a>
                    <FleetDocumentDeleteButton
                      documentId={doc.id}
                      fileName={doc.fileName}
                      subject={`für ${driver.data.displayName}`}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {complianceOn && driver.data.checks.length > 0 ? (
        <section className="space-y-3">
          <h2 className="text-base font-semibold">Bisherige Kontrollen</h2>
          <ul className={`${fleetTableFrameClassName} divide-y`}>
            {driver.data.checks.map((check) => (
              <li key={check.id} className="space-y-1 p-3 text-sm">
                <p className="font-medium">
                  {formatDeDate(check.checkedAt)} · {RESULT_LABELS[check.result]}
                </p>
                <p className="text-muted-foreground">
                  Klassen {check.classesConfirmed.join(', ')} · nächste Fälligkeit{' '}
                  {formatDeDate(check.nextCheckDue)}
                  {check.checkedByName ? ` · geprüft von ${check.checkedByName}` : null}
                </p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
